import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import config from '@efdigital/ll-admin-v2/config/environment';
import { DataTypeLti } from '@efdigital/ll-admin-v2/data/data-types/lti';
import { LTI_API_PATH } from '@efdigital/ll-admin-v2/data/handlers/lti/lti-consuming-platform-handler.ts';
import type { LtiConsumedTool } from '@efdigital/ll-admin-v2/data/types/lti';
import { withReactiveResponse } from '@warp-drive/core/request';
import {
  createRecord,
  deleteRecord,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { LtiOperation } from './ops.ts';

const apis: Apis = config['ll-addon-auth-apis'];

function consumedToolRequestBody(tool: LtiConsumedTool) {
  return {
    providedToolId: tool.providedToolId,
    platformData: tool.platformData,
  };
}

export function buildListConsumedTools(platformId: string) {
  const url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}/tools`;

  return withReactiveResponse<LtiConsumedTool[]>({
    url,
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    op: LtiOperation.ListConsumedTools,
    cacheOptions: {
      key: url,
      types: [DataTypeLti.ConsumedTool],
    },
  });
}

export function buildCreateConsumedTool(tool: LtiConsumedTool) {
  const platformId = tool.consumingPlatformId;
  if (!platformId) {
    throw new Error(
      'Cannot create a consumed tool without a consumingPlatformId'
    );
  }

  const init = createRecord(tool, {});

  init.url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}/tools`;
  init.body = JSON.stringify(consumedToolRequestBody(tool));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildUpdateConsumedTool(
  tool: LtiConsumedTool,
  platformId: string
) {
  const consumedToolId = tool.id;
  if (!consumedToolId) {
    throw new Error('Cannot update a consumed tool without an id');
  }

  const init = updateRecord(tool, {});

  init.url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}/tools/${encodeURIComponent(consumedToolId)}`;
  init.body = JSON.stringify(consumedToolRequestBody(tool));
  init.headers.set('Content-Type', 'application/json');

  return init;
}

export function buildDeleteConsumedTool(
  tool: LtiConsumedTool,
  platformId: string
) {
  const consumedToolId = tool.id;
  if (!consumedToolId) {
    throw new Error('Cannot delete a consumed tool without an id');
  }

  const init = deleteRecord(tool, {});

  init.url = `${apis.systemsApi.host}${LTI_API_PATH}/platforms/${encodeURIComponent(platformId)}/tools/${encodeURIComponent(consumedToolId)}`;
  init.headers.set('Content-Type', 'application/json');

  return init;
}
